import { action, computed, observable } from 'mobx';

import { DishGroupWithFeedbackAvailability } from 'http/Api/Feedback/types';
import Log from 'helpers/log';
import httpFacade from 'http/httpFacade';
import { PromiseService } from 'services/PromiseService';
import { sortByAccessor } from 'helpers/accessors';
import { SortConfig } from 'helpers/types';

import { IGroupTag } from 'components/Modals/TagsSelectModal/TagsSelectModal';

class FeedbackMenuItemsStore {
  @observable isInit = false;

  @observable tags: IGroupTag[] = [];
  @observable defaultSelectedTags: IGroupTag[] = [];

  @observable private _promiseService = new PromiseService();
  private _sortConfig: SortConfig = {
    accessor: 'title',
    desc: false,
  };

  @action
  init = async () => {
    try {
      const { data } = await httpFacade.feedback.fetchDishGroupAll();

      this.tags = this._getTags(data);
      this.defaultSelectedTags = this._getSelectedTags(data);
    } catch (e) {
      Log.error(e);
    } finally {
      this.isInit = true;
    }
  };

  submit = async (selectedTags: IGroupTag[]) => {
    const ids = this._getSelectedIds(selectedTags);

    try {
      await this._promiseService.add(() =>
        httpFacade.feedback.updateAvailableMenuItems(ids),
      );
    } catch (e) {
      Log.error(e);
    }
  };

  private _getSelectedIds = (tags: IGroupTag[]) => {
    const ids: string[] = [];

    for (const tag of tags) {
      if (!tag.isGroup) ids.push(tag.id);
    }

    return ids;
  };

  private _getTags = (dishGroups: DishGroupWithFeedbackAvailability[]) => {
    const tags: IGroupTag[] = [];

    for (const dishGroup of dishGroups) {
      const { id, title, items } = dishGroup;

      tags.push({ id, title, isGroup: true });

      for (const item of items) {
        tags.push({ id: item.id, title: item.title, groupId: id });
      }
    }

    tags.sort(sortByAccessor(this._sortConfig));

    return tags;
  };

  private _getSelectedTags = (
    dishGroups: DishGroupWithFeedbackAvailability[],
  ) => {
    const tags: IGroupTag[] = [];

    for (const dishGroup of dishGroups) {
      let isGroupSelected = true;

      for (const item of dishGroup.items) {
        if (!item.available) {
          isGroupSelected = false;
          continue;
        }

        tags.push({ id: item.id, title: item.title, groupId: dishGroup.id });
      }

      if (isGroupSelected) {
        tags.push({ id: dishGroup.id, title: dishGroup.title, isGroup: true });
      }
    }

    return tags;
  };

  @computed
  get loading() {
    return !this.isInit || this._promiseService.isPending;
  }
}

export default FeedbackMenuItemsStore;
